import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { primaryEntityActionButtonClass, secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { cn } from '@/lib/utils/helpers'
import modalStyles from '@/features/application/styles/modals.module.css'
import { dashboardTileFormSchema, type DashboardTileFormValues } from '../types/schemas'
import type { DashboardTile, DashboardTileEditModalProps } from '../types'
import iconLibrary from '../constants/iconLibrary.json'

const ICON_LIST: string[] = (iconLibrary as { icons: string[] }).icons

function coerceNewTab(value: DashboardTile['new_tab'] | undefined): boolean {
  if (typeof value === 'boolean') return value
  return String(value) === '1' || String(value).toLowerCase() === 'true'
}

export function DashboardTileEditModal({
  isOpen,
  tile,
  submitting,
  onClose,
  onSubmit,
}: DashboardTileEditModalProps) {
  /**
   * Mirrors Angular `dashboard-tiles-addedit`:
   * - `tile_icon` is NOT patched into the text field on edit (`delete tileDetails.tile_icon`).
   * - Full class string lives in `selectdIcon`; the input only has placeholder "Search Icon" for filtering.
   */
  const [iconSearch, setIconSearch] = useState('')
  const [selectedIcon, setSelectedIcon] = useState<string | undefined>(undefined)
  const [baselineIcon, setBaselineIcon] = useState<string | undefined>(undefined)

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm<DashboardTileFormValues>({
    resolver: zodResolver(dashboardTileFormSchema),
    mode: 'onChange',
    defaultValues: {
      tile_name: '',
      tile_description: '',
      icon_color: '#000000',
      font_color: '#000000',
      tile_url: '',
      new_tab: false,
    },
  })

  // eslint-disable-next-line react-hooks/incompatible-library -- react-hook-form watch() is intentionally unmemoizable
  const iconColor = watch('icon_color')

  useEffect(() => {
    if (!isOpen) return
    reset({
      tile_name: tile?.tile_name ?? '',
      tile_description: tile?.tile_description ?? '',
      icon_color: tile?.icon_color ?? '#000000',
      font_color: tile?.font_color ?? '#000000',
      tile_url: tile?.tile_url ?? '',
      new_tab: coerceNewTab(tile?.new_tab),
    })
    const saved = tile?.tile_icon?.trim() || undefined
    setSelectedIcon(saved)
    setBaselineIcon(saved)
    setIconSearch('')
  }, [isOpen, tile?.tile_id, reset])

  // Angular iconlist filter: show all when empty or when search contains "fa fa-", else substring match.
  const filteredIcons = useMemo(() => {
    const search = iconSearch.trim()
    if (!search) return ICON_LIST
    if (search.includes('fa fa-')) return ICON_LIST
    return ICON_LIST.filter((icon) => icon.includes(search))
  }, [iconSearch])

  const iconDirty = selectedIcon !== baselineIcon
  const canSubmit = isValid && (isDirty || iconDirty)

  const internalSubmit = (values: DashboardTileFormValues) => {
    onSubmit(
      {
        tile_name: values.tile_name.trim(),
        tile_description: values.tile_description.trim(),
        tile_icon: selectedIcon?.trim() || undefined,
        icon_color: values.icon_color,
        font_color: values.font_color,
        tile_url: values.tile_url.trim(),
        new_tab: values.new_tab ? '1' : '0',
      },
      tile?.tile_id,
    )
  }

  return (
    <OnScreenModal
      isOpen={isOpen}
      title={tile?.tile_id ? 'Edit Dashboard Tile' : 'Add Dashboard Tile'}
      onClose={onClose}
      maxWidth="max-w-2xl"
      bodyScrollOnly
      panelDataTestId="dashboard-tile-edit-modal-panel"
      footer={
        <div
          className="flex flex-wrap items-center justify-center gap-3"
          data-testid="dashboard-tile-edit-modal-footer"
        >
          <button
            type="submit"
            form="dashboard-tile-edit-form"
            disabled={!canSubmit || submitting}
            className={cn(primaryEntityActionButtonClass, 'min-w-[120px]')}
            data-testid="dashboard-tile-edit-submit"
          >
            {submitting ? 'Saving...' : 'Submit'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className={cn(secondaryEntityActionButtonClass, 'min-w-[120px]')}
            data-testid="dashboard-tile-edit-cancel"
          >
            Cancel
          </button>
        </div>
      }
    >
      <form
        id="dashboard-tile-edit-form"
        onSubmit={handleSubmit(internalSubmit)}
        className="space-y-4 p-2"
        noValidate
      >
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Tile Name</label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1597ff] focus:border-[#1597ff]"
            {...register('tile_name')}
          />
          {errors.tile_name && (
            <p className={modalStyles.formFieldError} role="alert">
              {errors.tile_name.message}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Tile Description</label>
          <textarea
            rows={5}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1597ff] focus:border-[#1597ff]"
            {...register('tile_description')}
          />
          {errors.tile_description && (
            <p className={modalStyles.formFieldError} role="alert">
              {errors.tile_description.message}
            </p>
          )}
        </div>

        <div className="grid grid-cols-[1fr_auto_auto] gap-3 items-end">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="dashboard-tile-icon-search">
              Tile Icon
            </label>
            <input
              id="dashboard-tile-icon-search"
              type="text"
              value={iconSearch}
              onChange={(e) => setIconSearch(e.target.value)}
              placeholder="Search Icon"
              autoComplete="off"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1597ff] focus:border-[#1597ff] text-sm"
            />
          </div>
          <div className="flex items-center justify-center h-10 w-10 border border-slate-200 rounded bg-slate-50 shrink-0">
            {selectedIcon ? (
              <i
                className={`${selectedIcon} text-lg leading-none`}
                style={{ color: iconColor }}
                aria-hidden="true"
              />
            ) : (
              <span className="text-xs text-slate-400">?</span>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Icon Color</label>
            <input
              type="color"
              className="h-10 w-16 border border-slate-300 rounded cursor-pointer"
              {...register('icon_color')}
            />
          </div>
        </div>

        <div
          className="grid grid-cols-6 gap-1 max-h-44 overflow-y-auto p-2 border border-slate-200 rounded bg-slate-50"
          role="listbox"
          aria-label="Icon picker"
        >
          {filteredIcons.map((icon) => (
            <button
              key={icon}
              type="button"
              onClick={() => setSelectedIcon(icon)}
              title={icon}
              className={`flex items-center justify-center h-9 rounded hover:bg-slate-200 ${
                icon === selectedIcon ? 'bg-button-soft ring-1 ring-button' : ''
              }`}
            >
              <i className={`${icon} text-base text-slate-700`} aria-hidden="true" />
            </button>
          ))}
          {filteredIcons.length === 0 && (
            <p className="col-span-6 text-xs text-slate-500 text-center py-2">No icons match.</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Background Color</label>
          <input
            type="color"
            className="h-10 w-16 border border-slate-300 rounded cursor-pointer"
            {...register('font_color')}
          />
        </div>

        <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">URL</label>
            <input
              type="text"
              placeholder="/company"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1597ff] focus:border-[#1597ff]"
              {...register('tile_url')}
            />
            {errors.tile_url && (
              <p className={modalStyles.formFieldError} role="alert">
                {errors.tile_url.message}
              </p>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 pb-2">
            <input type="checkbox" className="rounded" {...register('new_tab')} />
            In New Tab
          </label>
        </div>
      </form>
    </OnScreenModal>
  )
}
